import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Play, Pause, Music2, Mic } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toTitleCase } from '../utils/displayNormalization';

/**
 * HourClock — single SVG 60-minute pie that visualises one hour of
 * radio programming.
 *
 * v2.12.0 redesign:
 *   • Bigger size (1 clock per row, ~520px)
 *   • Score-category colours synced with the StreamMonitor live table
 *   • Breaks ALL share a single dark-slate colour (no severity gradient,
 *     operator complained the colour swap was confusing across clocks)
 *   • Click-to-pin (selected slice replaces hover)
 *   • Per-5min listener sparkline drawn ALONG THE INNER PERIMETER of
 *     the clock
 *   • Cover art + audio preview play button shown in the info panel
 *     when a music slice is selected
 *   • Slice carries a tiny SONG / BREAK badge when it's wide enough to
 *     fit one (>= 4 minutes ≈ 24°)
 */

const SONG_HEX = '#16a34a'; // green-600 for every song
// v2.13.21 (2026-02): Operator request — colour gaps by content_type.
// BREAK (15-300s sandwich) → amber, CONTENT_BLOCK (5-15min) → blue,
// PROGRAM_SEGMENT (>15min) → purple, UNKNOWN → slate grey.
const BREAK_HEX           = '#f59e0b'; // amber-500
const CONTENT_BLOCK_HEX   = '#3b82f6'; // blue-500
const PROGRAM_SEGMENT_HEX = '#a855f7'; // purple-500
const UNKNOWN_HEX         = '#4b5563'; // slate-600
// Back-compat aliases for older code paths that still reference the
// v2.12.9 NO_DATA palette (e.g. inner dial background). Once the
// frontend is fully migrated these can be removed.
const NO_DATA_HEX = UNKNOWN_HEX;
const TALK_HEX    = UNKNOWN_HEX;

const CONTENT_TYPE_COLOUR = {
  BREAK:           BREAK_HEX,
  CONTENT_BLOCK:   CONTENT_BLOCK_HEX,
  PROGRAM_SEGMENT: PROGRAM_SEGMENT_HEX,
  UNKNOWN:         UNKNOWN_HEX,
};

const CONTENT_TYPE_LABEL = {
  BREAK:           'BREAK',
  CONTENT_BLOCK:   'BLOCK',
  PROGRAM_SEGMENT: 'SHOW',
  UNKNOWN:         'NO DATA',
};

const polar = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arcPath = (cx, cy, r, startAngle, endAngle) => {
  const a = polar(cx, cy, r, startAngle);
  const b = polar(cx, cy, r, endAngle);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${a.x} ${a.y} A ${r} ${r} 0 ${large} 1 ${b.x} ${b.y} Z`;
};

const formatDuration = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return sec ? `${m}m ${sec}s` : `${m}m`;
};

/* ─────────────────────────────────────────────────────────────── */

const ListenerSparkline = ({ hour, curve, curveYesterday, t }) => {
  // Filter the global per-5min curve to the 12 buckets of THIS hour.
  const points = useMemo(() => {
    if (!Array.isArray(curve)) return [];
    return curve.filter((p) =>
      p.minute.startsWith(`${String(hour).padStart(2, '0')}:`),
    );
  }, [curve, hour]);
  const pointsYesterday = useMemo(() => {
    if (!Array.isArray(curveYesterday)) return [];
    return curveYesterday.filter((p) =>
      p.minute.startsWith(`${String(hour).padStart(2, '0')}:`),
    );
  }, [curveYesterday, hour]);

  const todayVals = points
    .map((p) => p.listeners)
    .filter((v) => v != null && v > 0);
  const yestVals = pointsYesterday
    .map((p) => p.listeners)
    .filter((v) => v != null && v > 0);
  if (todayVals.length < 2 && yestVals.length < 2) return null;

  const allVals = [...todayVals, ...yestVals];
  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const range = Math.max(1, maxV - minV);
  const W = 900;
  const H = 200;
  const padX = 56;
  const padY = 36;
  const step = (W - padX * 2) / 11;

  const buildPath = (pts) =>
    pts
      .map((p, i) => {
        if (p.listeners == null) return null;
        const x = padX + i * step;
        const y =
          H - padY - ((p.listeners - minV) / range) * (H - padY * 1.6);
        return { x, y, v: p.listeners, m: p.minute };
      })
      .filter(Boolean);

  const todayPath = buildPath(points);
  const yestPath = buildPath(pointsYesterday);

  const d = todayPath
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ');
  const dY = yestPath
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ');

  // Tick labels along the bottom (00, 05, 10, ..., 55)
  const minuteTicks = [];
  for (let m = 0; m <= 11; m += 1) {
    minuteTicks.push({
      x: padX + m * step,
      label: String(m * 5).padStart(2, '0'),
    });
  }

  return (
    <div className="w-full mt-3" data-testid={`hour-sparkline-${hour}`}>
      <div className="flex items-center justify-between text-[12px] text-white/55 mb-1 px-1">
        <span className="font-semibold">{t('clkView_sparklineTitle')}</span>
        <span className="tabular-nums flex items-center gap-3">
          {yestPath.length >= 2 && (
            <span className="inline-flex items-center gap-1.5 text-[11px]">
              <span
                className="inline-block w-3 h-0.5 rounded-full"
                style={{ backgroundColor: '#60a5fa' }}
              />
              {t('clkView_yesterday')}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-0.5 rounded-full"
              style={{ backgroundColor: '#fbbf24' }}
            />
            {t('clkView_today')}: {t('clkView_min')} {Math.min(...todayVals)} ·{' '}
            {t('clkView_max')} {Math.max(...todayVals)}
          </span>
        </span>
      </div>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* horizontal min/max grid lines */}
        <line
          x1={padX}
          y1={H - padY}
          x2={W - padX}
          y2={H - padY}
          stroke="#1e293b"
          strokeWidth="1"
        />
        <line
          x1={padX}
          y1={padY * 0.6}
          x2={W - padX}
          y2={padY * 0.6}
          stroke="#1e293b"
          strokeWidth="0.7"
          strokeDasharray="3 4"
        />
        {/* y-axis tick labels (left, larger) */}
        <text
          x={padX - 6}
          y={padY * 0.7 + 4}
          textAnchor="end"
          fontSize="13"
          fill="#94a3b8"
          fontFamily="monospace"
        >
          {maxV}
        </text>
        <text
          x={padX - 6}
          y={H - padY + 5}
          textAnchor="end"
          fontSize="13"
          fill="#94a3b8"
          fontFamily="monospace"
        >
          {minV}
        </text>

        {/* Yesterday curve (drawn first so it stays behind today) */}
        {yestPath.length >= 2 && (
          <>
            <path
              d={dY}
              fill="none"
              stroke="#60a5fa"
              strokeWidth="1.8"
              strokeDasharray="5 4"
              opacity="0.75"
            />
            {yestPath.map((p, i) => (
              <circle
                key={`y-${i}`}
                cx={p.x}
                cy={p.y}
                r="2.2"
                fill="#60a5fa"
                opacity="0.8"
              >
                <title>{`${p.m} · ${p.v} ${t('clkView_listenersUnit')} (${t('clkView_yesterday')})`}</title>
              </circle>
            ))}
          </>
        )}

        {/* Today curve */}
        <path d={d} fill="none" stroke="#fbbf24" strokeWidth="2.4" />
        {todayPath.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3.2" fill="#fbbf24">
              <title>{`${p.m} · ${p.v} ${t('clkView_listenersUnit')}`}</title>
            </circle>
            <text
              x={p.x}
              y={p.y - 10}
              textAnchor="middle"
              fontSize="12"
              fill="#fde68a"
              fontFamily="monospace"
              fontWeight="700"
            >
              {p.v}
            </text>
          </g>
        ))}

        {/* Time ticks every 5 min (all 12) */}
        {minuteTicks.map((t2, i) => (
          <g key={i}>
            <line
              x1={t2.x}
              y1={H - padY}
              x2={t2.x}
              y2={H - padY + 4}
              stroke="#475569"
              strokeWidth="1"
            />
            <text
              x={t2.x}
              y={H - padY + 18}
              textAnchor="middle"
              fontSize="11"
              fill="#94a3b8"
              fontFamily="monospace"
            >
              :{t2.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────── */

const CoverPlayButton = ({ coverUrl, previewUrl, artist, title }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  // v2.13.19 (2026-02) — Operator bug report:
  // "When I click a 2nd song the cover/title/artist change but the
  //  audio keeps playing the FIRST song I clicked."
  // ROOT CAUSE: the toggle() handler only constructs the Audio object
  // when `audioRef.current` is null, but never refreshes it when the
  // `previewUrl` prop changes. The Audio object was being recycled
  // with the original URL forever. FIX: when previewUrl changes, pause
  // the old audio AND drop the ref so toggle() creates a new Audio
  // bound to the new URL.
  useEffect(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        /* ignore */
      }
      // Detach handlers and drop the ref — the next toggle() will
      // create a fresh Audio bound to the current previewUrl.
      audioRef.current = null;
    }
    setPlaying(false);
  }, [previewUrl]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!previewUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(previewUrl);
      audioRef.current.addEventListener('ended', () => setPlaying(false));
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  return (
    <div
      className="flex items-center gap-2 shrink-0"
      data-testid="hour-clock-cover-wrap"
    >
      <div
        className="flex items-center justify-center w-14 h-14 rounded-md overflow-hidden bg-slate-800 border border-white/10 relative"
        data-testid="hour-clock-cover"
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={`${artist || ''} ${title || ''}`.trim()}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <Music2 className="w-5 h-5 text-white/35" />
        )}
      </div>
      {previewUrl && (
        <button
          type="button"
          onClick={toggle}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-400/40 text-emerald-300 transition-colors"
          aria-label={playing ? 'Pause preview' : 'Play preview'}
          data-testid="hour-clock-preview-play"
        >
          {playing ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────── */

const HourClock = ({ hour, date, events, listenerCurve, listenerCurveYesterday }) => {
  const { t } = useLanguage();
  const [selectedIdx, setSelectedIdx] = useState(null);
  const detailsRef = useRef(null);

  // Compute slices. Each event takes (duration / 3600) * 360°.
  const slices = useMemo(() => {
    if (!events?.length) return [];
    const hourStartS = hour * 3600;
    return events
      .map((ev, idx) => {
        const dt = new Date(ev.start);
        if (Number.isNaN(dt.getTime())) return null;
        const localS =
          dt.getHours() * 3600 +
          dt.getMinutes() * 60 +
          dt.getSeconds();
        // Phase 2 SOT — prefer `visual_duration_seconds` for arc size so
        // songs without a persisted raw duration still render an arc based
        // on the bounded look-ahead inference. Falls back to raw
        // `duration_seconds` when the backend has not yet emitted the
        // Phase 2 field (older payloads).
        const dur =
          ev.visual_duration_seconds || ev.duration_seconds || 0;
        if (!dur) return null;
        let startS = localS - hourStartS;
        let endS = startS + dur;
        startS = Math.max(0, startS);
        endS = Math.min(3600, endS);
        if (endS <= startS) return null;
        // v2.13.110 (2026-07-03) — GRACE PERIOD @ :00.
        // Cross-hour gap fragments that spill only a few seconds
        // into the new hour render as a tiny sliver at the top-of-clock
        // position (12 o'clock). Operator flagged these repeatedly on
        // 2026-07-03 as visual noise ("κόκκινοι κύκλοι στο :00"). Any
        // slice whose visible portion in THIS hour is < 30 s AND that
        // starts within the first 30 s of the hour is polling-drift
        // noise from the previous hour's break tail — suppress it
        // from rendering so the clock reads cleanly.
        const visibleDur = endS - startS;
        const startsAtHourTop = startS < 30;
        if (startsAtHourTop && visibleDur < 30) return null;
        const startDeg = (startS / 3600) * 360;
        const endDeg = (endS / 3600) * 360;
        return { ev, startDeg, endDeg, idx, durS: dur };
      })
      .filter(Boolean);
  }, [events, hour]);

  // Hour-net listener summary
  const hourNet = useMemo(() => {
    const firstMusic = events?.find(
      (e) => e.type === 'music' && e.start_listeners != null,
    );
    const lastMusic = [...(events || [])]
      .reverse()
      .find((e) => e.type === 'music' && e.end_listeners != null);
    if (!firstMusic || !lastMusic || !firstMusic.start_listeners) {
      return null;
    }
    const sl = firstMusic.start_listeners;
    const el = lastMusic.end_listeners;
    const delta = el - sl;
    const pct = (delta / sl) * 100;
    return { sl, el, delta, pct };
  }, [events]);

  // Compact, mobile-friendly SVG (v2.12.7, 2026-06-16): smaller
  // footprint than v2.12.5 so the clock + info-panel fit on one
  // screen without scrolling on a 1080p desktop and don't clip on
  // mobile portrait. Font sizes / colours kept identical to v2.12.5
  // per operator instruction.
  const W = 540;
  const H = 540;
  const cx = W / 2;
  const cy = H / 2;
  const R = 230;
  const labelR = R + 24;

  const counts = useMemo(() => {
    const m = events?.filter((e) => e.type === 'music').length || 0;
    // v2.12.9: collapse break+talk counts into a single "no-data" total.
    const nd = events?.filter((e) => e.type === 'gap').length || 0;
    return { m, nd };
  }, [events]);

  const selected = selectedIdx != null
    ? slices.find((s) => s.idx === selectedIdx)
    : null;

  const handleSliceClick = (sliceIdx) => {
    setSelectedIdx((cur) => {
      const next = cur === sliceIdx ? null : sliceIdx;
      // On narrow viewports the info panel sits BELOW the clock, so
      // bring it into view automatically — operator complained they
      // had to scroll-down to see the selected song.
      if (next != null && detailsRef.current) {
        const isNarrow =
          typeof window !== 'undefined' && window.innerWidth < 1024;
        if (isNarrow) {
          // Defer to allow React to render the panel content first.
          setTimeout(() => {
            try {
              detailsRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });
            } catch {
              /* ignore */
            }
          }, 50);
        }
      }
      return next;
    });
  };

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 w-full"
      data-testid={`hour-clock-${hour}`}
    >
      <div className="flex items-baseline justify-between w-full mb-3 flex-wrap gap-2">
        <div>
          <div className="text-3xl font-mono font-bold text-white">
            {String(hour).padStart(2, '0')}:00
          </div>
          <div className="text-sm text-white/70">{date}</div>
        </div>
        <div className="text-sm text-white text-right">
          <div className="text-xs text-white/65 uppercase tracking-wide">
            {t('clkView_thisHourLabel')}
          </div>
          <div className="text-base">
            <span className="font-bold text-white">{counts.m}</span>{' '}
            {t('clkView_songs')} ·{' '}
            <span className="font-bold text-white">{counts.nd}</span>{' '}
            {t('clkView_noData')}
          </div>
          {hourNet && (
            <div
              className="text-sm tabular-nums mt-1 font-semibold"
              style={{
                color:
                  hourNet.delta < 0
                    ? '#fca5a5'
                    : hourNet.delta > 0
                    ? '#86efac'
                    : '#e2e8f0',
              }}
            >
              {t('clkView_netHour')}:{' '}
              <span className="font-bold">
                {hourNet.delta > 0 ? '+' : ''}
                {hourNet.delta}
              </span>{' '}
              {t('clkView_listenersUnit')} (
              {hourNet.pct > 0 ? '+' : ''}
              {hourNet.pct.toFixed(1)}%)
            </div>
          )}
        </div>
      </div>

      {/* v2.12.7 — clock + info panel side-by-side on desktop so a
          selected slice is ALWAYS visible without scrolling.  On
          tablets / mobile the info panel stacks below the clock
          (still close because the clock footprint is small).
          v2.13.19 (2026-02): Force `justify-items-center` so the
          clock stays visually centred on narrow viewports even after
          the info panel mounts below — operator reported a "shift
          left" feeling when a slice was selected. */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start justify-items-center lg:justify-items-stretch">
        <div className="flex justify-center w-full mx-auto">
        <svg
          width="100%"
          height="auto"
          style={{ maxWidth: `${W}px`, maxHeight: `${H}px` }}
          /* v2.13.19 (2026-02): Slightly wider horizontal padding so
             outside labels (NO DATA on narrow gaps with leader lines)
             never get clipped at the 0°/180° edges. */
          viewBox={`-90 0 ${W + 180} ${H}`}
          preserveAspectRatio="xMidYMid meet"
        >
        {/* Outer ring */}
        <circle cx={cx} cy={cy} r={R + 6} fill="#1e293b" />
        {/* Inner dial background = TALK colour so micro-gaps between
            recorded events (< min_gap_seconds, e.g. 2-15s polling
            jitter) blend in with the "anything else" category instead
            of looking like un-labeled black holes. Operator confirmed:
            a 30-second silent slot between two songs is NEVER an ad
            block (operator's rule: 60-240s @ slot windows). */}
        <circle cx={cx} cy={cy} r={R} fill={TALK_HEX} />

        {/* Slices */}
        {slices.map((s) => {
          let fill;
          let labelText;
          let glyph;
          if (s.ev.type === 'music') {
            fill = SONG_HEX;
            labelText = 'SONG';
            glyph = '♪';
          } else {
            // v2.13.21 (2026-02): Operator-driven content_type
            // colour palette. The backend now distinguishes BREAK
            // (15-300s sandwich) / CONTENT_BLOCK (5-15min) /
            // PROGRAM_SEGMENT (>15min) / UNKNOWN. Each gets its own
            // hue so the operator can spot a 48-minute show on the
            // clock at a glance.
            //
            // v2.13.69 (2026-06-25): Domain-rule BREAK promotion.
            // The backend now sets `ev.is_break = true` on any gap
            // whose START minute falls in :20 ±5 or :50 ±5 AND
            // whose duration is ≥ 60 s (operator-defined ad-break
            // window — covers 90 %+ of Greek stations). When that
            // flag is true we ALWAYS render the slice as BREAK
            // (amber), regardless of classifier `content_type`,
            // because the rule itself is the higher-confidence
            // signal in shadow-mode production. The `content_type`
            // palette is still honoured for non-`is_break` gaps so
            // long programme blocks keep their distinct colours.
            const ctype = (s.ev.content_type || 'UNKNOWN').toUpperCase();
            if (s.ev.is_break === true) {
              fill = BREAK_HEX;
              labelText = 'BREAK';
            } else {
              fill = CONTENT_TYPE_COLOUR[ctype] || UNKNOWN_HEX;
              labelText = CONTENT_TYPE_LABEL[ctype] || 'NO DATA';
            }
            glyph = '○';
          }
          const isSelected = selectedIdx === s.idx;
          const arcSpan = s.endDeg - s.startDeg;
          // v2.13.19 (2026-02) — Operator request:
          // The Greek label "ΧΩΡΙΣ ΔΕΔΟΜΕΝΑ" (14 chars) overflows the
          // slice on every non-music gap. To keep readability across
          // ALL languages without dropping localisation, we make the
          // inside-threshold proportional to the label length so long
          // localised strings are always pushed OUTSIDE with a leader
          // line. SONG (4 chars) keeps the original 10° behaviour.
          const inside_min_deg = Math.max(10, labelText.length * 2.6);
          const showTextInside = arcSpan >= inside_min_deg;
          // Anything wider than 4° but narrower than `inside_min_deg`
          // → label is pushed OUTSIDE the clock with a leader line so
          // the operator can still see what the slice is.
          const showTextOutside = !showTextInside && arcSpan >= 3 && s.ev.type !== 'music';
          const midDeg = (s.startDeg + s.endDeg) / 2;
          const badgePos = polar(cx, cy, R * 0.7, midDeg);
          // Outside-label geometry: anchor point on the circle edge +
          // small radial leader + label at labelR + 18
          const edgeP = polar(cx, cy, R, midDeg);
          const labelP = polar(cx, cy, R + 36, midDeg);
          const isRight = midDeg < 180;
          return (
            <g key={s.idx}>
              <path
                d={arcPath(cx, cy, R, s.startDeg, s.endDeg)}
                fill={fill}
                stroke={isSelected ? '#fde047' : 'rgba(255,255,255,0.55)'}
                strokeWidth={isSelected ? 2.5 : (s.ev.type === 'gap' ? 1 : 0.5)}
                onClick={() => handleSliceClick(s.idx)}
                style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                opacity={
                  selectedIdx != null && selectedIdx !== s.idx ? 0.4 : 1
                }
                data-testid={`clock-slice-${hour}-${s.idx}`}
              />
              {/* Phase 3 marker — song sits INSIDE a consolidated BREAK
                  cluster (bridge / jingle). Preserved (never deleted)
                  and surfaced as an additive dashed amber outline so
                  the operator can spot it visually while auditing the
                  clock. See _apply_break_annotations in
                  backend/routes/broadcast_insights_routes.py. */}
              {s.ev.type === 'music' && s.ev.part_of_break_cluster === true && (
                <path
                  d={arcPath(cx, cy, R, s.startDeg, s.endDeg)}
                  fill="none"
                  stroke={BREAK_HEX}
                  strokeWidth="2"
                  strokeDasharray="4 3"
                  pointerEvents="none"
                  opacity={
                    selectedIdx != null && selectedIdx !== s.idx ? 0.4 : 0.9
                  }
                  data-testid={`clock-slice-${hour}-${s.idx}-break-cluster-marker`}
                />
              )}
              {showTextInside && (
                <text
                  x={badgePos.x}
                  y={badgePos.y + 4}
                  textAnchor="middle"
                  fontSize={arcSpan >= 22 ? '13' : '10'}
                  fontWeight="800"
                  fill="#ffffff"
                  pointerEvents="none"
                  style={{ letterSpacing: arcSpan >= 22 ? '0.6px' : '0.2px' }}
                >
                  {labelText}
                </text>
              )}
              {showTextOutside && (
                <g pointerEvents="none">
                  <line
                    x1={edgeP.x}
                    y1={edgeP.y}
                    x2={labelP.x}
                    y2={labelP.y}
                    stroke="#e2e8f0"
                    strokeWidth="1"
                  />
                  <circle cx={edgeP.x} cy={edgeP.y} r="2" fill="#e2e8f0" />
                  <text
                    x={labelP.x}
                    y={labelP.y + 4}
                    textAnchor={isRight ? 'start' : 'end'}
                    fontSize="12"
                    fontWeight="700"
                    fill="#ffffff"
                    style={{ letterSpacing: '0.5px' }}
                  >
                    {labelText}
                  </text>
                </g>
              )}
              {!showTextInside && !showTextOutside && arcSpan >= 2 && (
                <text
                  x={badgePos.x}
                  y={badgePos.y + 5}
                  textAnchor="middle"
                  fontSize="14"
                  fontWeight="700"
                  fill="#ffffff"
                  pointerEvents="none"
                >
                  {glyph}
                </text>
              )}
            </g>
          );
        })}

        {/* Minute ticks (every 5 min) */}
        {Array.from({ length: 12 }).map((_, m) => {
          const angle = m * 30;
          const inner = polar(cx, cy, R + 2, angle);
          const outer = polar(cx, cy, R + 10, angle);
          const label = polar(cx, cy, labelR, angle);
          return (
            <g key={m}>
              <line
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke="#94a3b8"
                strokeWidth={1.2}
              />
              <text
                x={label.x}
                y={label.y + 5}
                textAnchor="middle"
                fontSize="15"
                fontWeight="500"
                fill="#e2e8f0"
                fontFamily="monospace"
              >
                {m * 5}
              </text>
            </g>
          );
        })}

        {/* Centre disc */}
        <circle cx={cx} cy={cy} r={R * 0.34} fill="#0b1220" stroke="#475569" strokeWidth={1.5} />
        <text
          x={cx}
          y={cy + (hourNet ? -8 : 12)}
          textAnchor="middle"
          fontSize="38"
          fontWeight="700"
          fill="#ffffff"
          fontFamily="monospace"
        >
          {String(hour).padStart(2, '0')}:00
        </text>
        {hourNet && (
          <>
            <text
              x={cx}
              y={cy + 16}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#cbd5e1"
              style={{ letterSpacing: '0.8px' }}
            >
              {t('clkView_netHour').toUpperCase()}
            </text>
            <text
              x={cx}
              y={cy + 38}
              textAnchor="middle"
              fontSize="18"
              fontWeight="700"
              fill={
                hourNet.delta < 0
                  ? '#fca5a5'
                  : hourNet.delta > 0
                  ? '#86efac'
                  : '#94a3b8'
              }
            >
              {hourNet.delta > 0 ? '+' : ''}
              {hourNet.delta}{' '}
              <tspan fontSize="15" fontWeight="600">
                ({hourNet.pct > 0 ? '+' : ''}
                {hourNet.pct.toFixed(1)}%)
              </tspan>
            </text>
          </>
        )}
      </svg>
      </div>

      {/* Info panel column — same row as the clock on lg+, wraps
          below on smaller screens.  ScrollIntoView fires on click
          on narrow viewports (handled in handleSliceClick). */}
      <div
        ref={detailsRef}
        className="text-sm text-white/85 lg:max-h-[540px] lg:overflow-y-auto"
        data-testid={`hour-clock-${hour}-details`}
      >
        {selected ? (
          selected.ev.type === 'music' ? (
            <div className="flex gap-3 items-start bg-white/5 border border-white/10 rounded-lg p-3">
              <CoverPlayButton
                coverUrl={selected.ev.cover_art_url}
                previewUrl={selected.ev.preview_url}
                artist={selected.ev.artist}
                title={selected.ev.title}
              />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                    style={{
                      backgroundColor: SONG_HEX + '33',
                      color: SONG_HEX,
                    }}
                  >
                    {t('clkView_song')}
                  </span>
                  {/* Phase 3 additive marker — bridge/jingle inside a
                      consolidated BREAK cluster. Never suppresses the
                      song row; simply annotates it. */}
                  {selected.ev.part_of_break_cluster === true && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      style={{
                        backgroundColor: BREAK_HEX + '33',
                        color: BREAK_HEX,
                      }}
                      title="Phase 3 marker: song preserved inside a consolidated BREAK cluster (bridge/jingle)"
                      data-testid="song-in-break-cluster-badge"
                    >
                      IN BREAK
                    </span>
                  )}
                  <span className="text-[11px] text-white/40 tabular-nums">
                    {new Date(selected.ev.start).toLocaleTimeString('el-GR', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}{' '}
                    · {formatDuration(selected.durS)}
                  </span>
                </div>
                <div className="text-[15px] leading-snug break-words" data-testid="clock-song-title-line">
                  <span className="font-semibold">
                    {toTitleCase(selected.ev.artist || '')}
                  </span>
                  <span className="text-white/40 mx-1.5">—</span>
                  <span className="text-white/90">
                    {toTitleCase(selected.ev.title || '')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-white/60">
                  <span>
                    <span className="text-white/40">
                      {t('clkView_retention')}:
                    </span>{' '}
                    <span className="tabular-nums">
                      {selected.ev.retention_rate != null
                        ? `${selected.ev.retention_rate}%`
                        : '—'}
                    </span>
                  </span>
                  <span>
                    <span className="text-white/40">
                      {t('clkView_earlyDrop')}:
                    </span>{' '}
                    <span className="tabular-nums">
                      {selected.ev.early_drop_rate != null
                        ? `${selected.ev.early_drop_rate}%`
                        : '—'}
                    </span>
                  </span>
                  <span>
                    <span className="text-white/40">
                      {t('clkView_lsi')}:
                    </span>{' '}
                    <span className="tabular-nums">
                      {selected.ev.lsi != null ? selected.ev.lsi : '—'}
                    </span>
                  </span>
                  <span>
                    <span className="text-white/40">
                      {t('clkView_score')}:
                    </span>{' '}
                    <span className="tabular-nums">
                      {selected.ev.play_score != null
                        ? selected.ev.play_score
                        : '—'}
                    </span>
                  </span>
                </div>
                <div className="text-[12px] text-white/70">
                  <span className="text-white/40">
                    {t('clkView_listenerDeltaLabel')}:
                  </span>{' '}
                  <span className="tabular-nums">
                    {selected.ev.start_listeners != null &&
                    selected.ev.end_listeners != null
                      ? `${selected.ev.start_listeners} → ${selected.ev.end_listeners}`
                      : '—'}
                  </span>
                  {selected.ev.peak_listeners != null && (
                    <span className="text-white/40 ml-2">
                      ({t('clkView_listenersPeak')}{' '}
                      {selected.ev.peak_listeners})
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Mic className="w-4 h-4 text-white/55" />
                {(() => {
                  const ctype = (selected.ev.content_type || 'UNKNOWN').toUpperCase();
                  const bg = CONTENT_TYPE_COLOUR[ctype] || UNKNOWN_HEX;
                  const labelKey = {
                    BREAK: 'clkView_break',
                    CONTENT_BLOCK: 'clkView_contentBlock',
                    PROGRAM_SEGMENT: 'clkView_programSegment',
                    UNKNOWN: 'clkView_noData',
                  }[ctype] || 'clkView_noData';
                  return (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      style={{ backgroundColor: bg, color: '#ffffff' }}
                      data-testid={`gap-content-type-${ctype.toLowerCase()}`}
                    >
                      {t(labelKey)}
                    </span>
                  );
                })()}
                <span className="text-[11px] text-white/40 ml-2 tabular-nums">
                  {new Date(selected.ev.start).toLocaleTimeString('el-GR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}{' '}
                  · {formatDuration(selected.durS)}
                </span>
              </div>
              {selected.ev.classifier_sample_count != null && (
                <div className="text-[11px] text-white/45 tabular-nums">
                  {selected.ev.classifier_sample_count} samples ·{' '}
                  {selected.ev.classifier_coverage_pct?.toFixed?.(0) ?? 0}% coverage
                </div>
              )}
              <div className="text-[11px] text-white/50 italic">
                {t('clkView_noDataHint')}
              </div>
            </div>
          )
        ) : (
          <div className="text-white/30 italic text-[11px] text-center py-2">
            {t('clkView_clickToSee')}
          </div>
        )}
      </div>
      </div>

      {/* Sparkline of per-5min listener curve for this hour
          — full-width below the clock+info grid. */}
      <ListenerSparkline
        hour={hour}
        curve={listenerCurve}
        curveYesterday={listenerCurveYesterday}
        t={t}
      />
    </div>
  );
};

export default HourClock;
